import Typo from 'typo-js';

const HIGHLIGHT_NAME = 'wf-spell-errors';
const WORD_RE = /[A-Za-z][A-Za-z'\u2019-]{1,}/g;
const MIN_WORD_LEN = 3;
const DEBOUNCE_MS = 350;

/** Common academic / tech tokens that Tri-gram dictionarie often flag */
const ALLOWLIST = new Set(
	[
		'APA',
		'IRB',
		'Doi',
		'DOI',
		'URL',
		'HTML',
		'PDF',
		'JSON',
		'API',
		'PhD',
		'GPA',
		'Toc',
		'TOC',
		'WriteForm',
		'TinyMCE',
		'Et',
		'Al',
		'et',
		'al',
	].map((w) => w.toLowerCase())
);

let dictionaryPromise = null;

function dictionariesBaseUrl() {
	const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
	return `${base}/dictionaries`;
}

function loadDictionary() {
	if (!dictionaryPromise) {
		const base = dictionariesBaseUrl();
		dictionaryPromise = Promise.all([
			fetch(`${base}/en_US.aff`).then((r) => {
				if (!r.ok) throw new Error('Failed to load spelling .aff');
				return r.text();
			}),
			fetch(`${base}/en_US.dic`).then((r) => {
				if (!r.ok) throw new Error('Failed to load spelling .dic');
				return r.text();
			}),
		])
			.then(([aff, dic]) => new Typo('en_US', aff, dic))
			.catch((err) => {
				dictionaryPromise = null;
				console.warn('WriteForm spellcheck dictionary failed to load:', err);
				throw err;
			});
	}
	return dictionaryPromise;
}

function shouldSkipTextNode(node) {
	if (!node?.parentElement) return true;
	const el = node.parentElement;
	if (el.closest('code, pre, kbd, samp, .mce-toc, .mce-pagebreak, [data-mce-bogus], .wf-section-guide')) {
		return true;
	}
	// Skip non-editable chrome inside the document
	if (el.isContentEditable === false) return true;
	return false;
}

function isProbablyProperNoun(word, index, fullText) {
	if (!/^[A-Z][a-z''-]+$/.test(word)) return false;
	// Keep checking sentence starts; skip mid-sentence capitals (names)
	if (index === 0) return false;
	const before = fullText.slice(0, index).trimEnd();
	return !/[.!?…]["')\]]*$/.test(before);
}

function isMisspelled(dict, word) {
	if (!word || word.length < MIN_WORD_LEN) return false;
	if (ALLOWLIST.has(word.toLowerCase())) return false;
	if (/^([A-Z]{2,}|[A-Z][a-z]+[A-Z])/.test(word) && word.length <= 6) {
		// short acronyms / camel tokens
		if (word === word.toUpperCase()) return false;
	}
	if (dict.check(word)) return false;
	if (dict.check(word.toLowerCase())) return false;
	if (dict.check(word.toUpperCase())) return false;
	return true;
}

/**
 * Collect misspelled word ranges inside the TinyMCE iframe document.
 * @param {import('tinymce').Editor} editor
 * @param {Typo} dict
 * @returns {Range[]}
 */
function collectMisspelledRanges(editor, dict) {
	const doc = editor.getDoc();
	const body = editor.getBody();
	if (!doc || !body) return [];

	const view = doc.defaultView || editor.getWin();
	const NF = view?.NodeFilter;
	if (!NF) return [];

	const ranges = [];
	const walker = doc.createTreeWalker(body, NF.SHOW_TEXT, {
		acceptNode(node) {
			if (!node?.nodeValue || !node.nodeValue.trim()) return NF.FILTER_REJECT;
			if (shouldSkipTextNode(node)) return NF.FILTER_REJECT;
			return NF.FILTER_ACCEPT;
		},
	});

	let node;
	while ((node = walker.nextNode())) {
		const text = node.nodeValue || '';
		WORD_RE.lastIndex = 0;
		let match;
		while ((match = WORD_RE.exec(text))) {
			const word = match[0];
			if (isProbablyProperNoun(word, match.index, text)) continue;
			if (!isMisspelled(dict, word)) continue;

			try {
				const range = doc.createRange();
				range.setStart(node, match.index);
				range.setEnd(node, match.index + word.length);
				ranges.push(range);
			} catch {
				/* ignore invalid ranges */
			}
		}
	}

	return ranges;
}

function clearHighlights(win) {
	if (win?.CSS?.highlights) {
		win.CSS.highlights.delete(HIGHLIGHT_NAME);
	}
}

/**
 * DOM underline fallback when CSS Highlight API is unavailable.
 * Marks are stripped from serialized content.
 */
function clearDomMarks(editor) {
	const body = editor.getBody();
	if (!body) return;
	body.querySelectorAll('span.wf-spell-error').forEach((el) => {
		const parent = el.parentNode;
		if (!parent) return;
		while (el.firstChild) parent.insertBefore(el.firstChild, el);
		parent.removeChild(el);
		parent.normalize?.();
	});
}

function applyDomMarks(editor, ranges) {
	const doc = editor.getDoc();
	// Apply from end to start so offsets stay valid within the same text node
	const sorted = [...ranges].sort((a, b) => {
		try {
			return -a.compareBoundaryPoints(Range.START_TO_START, b);
		} catch {
			return 0;
		}
	});

	sorted.forEach((range) => {
		try {
			const span = doc.createElement('span');
			span.className = 'wf-spell-error';
			span.setAttribute('data-wf-spell', '1');
			range.surroundContents(span);
		} catch {
			/* surroundContents fails on partial element selections — skip */
		}
	});
}

function getWordAtCaret(editor) {
	const rng = editor.selection.getRng();
	if (!rng || !rng.collapsed) return null;
	const node = rng.startContainer;
	if (!node || node.nodeType !== Node.TEXT_NODE) return null;
	const text = node.nodeValue || '';
	const offset = rng.startOffset;
	WORD_RE.lastIndex = 0;
	let match;
	while ((match = WORD_RE.exec(text))) {
		const start = match.index;
		const end = start + match[0].length;
		if (offset >= start && offset <= end) {
			return { word: match[0], node, start, end };
		}
	}
	return null;
}

function replaceWordAt(editor, info, replacement) {
	const { node, start, end } = info;
	const text = node.nodeValue || '';
	node.nodeValue = `${text.slice(0, start)}${replacement}${text.slice(end)}`;
	const doc = editor.getDoc();
	const rng = doc.createRange();
	const caret = start + replacement.length;
	rng.setStart(node, caret);
	rng.setEnd(node, caret);
	editor.selection.setRng(rng);
	editor.nodeChanged();
	editor.undoManager?.add?.();
	editor.fire('input');
}

/**
 * Attach as-you-type spell checking to a TinyMCE editor instance.
 * Uses CSS Highlight API when available; falls back to inline marks.
 */
export function attachEditorSpellcheck(editor) {
	if (!editor || editor.__wfSpellcheckAttached) return;
	editor.__wfSpellcheckAttached = true;

	let timer = null;
	let dict = null;
	let applying = false;
	let lastSuggestionIds = [];

	const runCheck = async () => {
		if (applying || editor.removed) return;
		try {
			if (!dict) dict = await loadDictionary();
			if (editor.removed || applying) return;

			applying = true;
			try {
				const bookmark = editor.selection?.getBookmark?.(2, true);
				// Unwrap previous marks before measuring, otherwise ranges go stale
				clearDomMarks(editor);
				const ranges = collectMisspelledRanges(editor, dict);
				applyDomMarks(editor, ranges);
				clearHighlights(editor.getWin());
				if (bookmark) {
					try {
						editor.selection.moveToBookmark(bookmark);
					} catch {
						/* ignore */
					}
				}
			} finally {
				applying = false;
			}
		} catch (err) {
			applying = false;
			console.warn('WriteForm spellcheck skipped:', err?.message || err);
		}
	};

	const schedule = () => {
		if (applying) return;
		if (timer) window.clearTimeout(timer);
		timer = window.setTimeout(runCheck, DEBOUNCE_MS);
	};

	editor.on('init', () => {
		try {
			const body = editor.getBody();
			body?.setAttribute('spellcheck', 'false');
			body?.setAttribute('lang', 'en-US');
			editor.dom.addStyle(`
				span.wf-spell-error {
					text-decoration: wavy underline #e11d48 !important;
					text-decoration-skip-ink: none;
					background-color: rgba(225, 29, 72, 0.08);
				}
				::highlight(wf-spell-errors) {
					text-decoration-line: underline;
					text-decoration-style: wavy;
					text-decoration-color: #e11d48;
					background-color: rgba(225, 29, 72, 0.08);
				}
			`);
		} catch {
			/* ignore */
		}
		schedule();
	});

	editor.on('SetContent keyup paste Undo Redo', schedule);

	editor.on('remove', () => {
		if (timer) window.clearTimeout(timer);
		try {
			clearHighlights(editor.getWin());
		} catch {
			/* ignore */
		}
	});

	// Strip DOM fallback marks from HTML output (save / export / getContent)
	editor.on('GetContent', (e) => {
		if (!e.content || !e.content.includes('wf-spell-error')) return;
		try {
			const doc = new DOMParser().parseFromString(e.content, 'text/html');
			doc.querySelectorAll('span.wf-spell-error').forEach((el) => {
				const parent = el.parentNode;
				if (!parent) return;
				while (el.firstChild) parent.insertBefore(el.firstChild, el);
				parent.removeChild(el);
			});
			e.content = doc.body.innerHTML;
		} catch {
			/* ignore */
		}
	});

	// Right-click suggestions
	editor.ui.registry.addContextMenu('wfspell', {
		update: () => {
			lastSuggestionIds.forEach((id) => {
				try {
					// TinyMCE has no unregister; overwrite with noop items below
					void id;
				} catch {
					/* ignore */
				}
			});
			lastSuggestionIds = [];

			if (!dict) return '';
			const info = getWordAtCaret(editor);
			if (!info || !isMisspelled(dict, info.word)) return '';

			const suggestions = (dict.suggest(info.word) || []).slice(0, 5);
			if (!suggestions.length) {
				const id = 'wfspell_nosuggest';
				editor.ui.registry.addMenuItem(id, {
					text: `No suggestions for “${info.word}”`,
					onAction: () => {},
				});
				lastSuggestionIds = [id];
				return id;
			}

			const ids = suggestions.map((suggestion, index) => {
				const id = `wfspell_sug_${index}`;
				editor.ui.registry.addMenuItem(id, {
					text: suggestion,
					onAction: () => {
						replaceWordAt(editor, info, suggestion);
						schedule();
					},
				});
				return id;
			});
			lastSuggestionIds = ids;
			return ids.join(' ');
		},
	});
}

export default attachEditorSpellcheck;
